import {Grid, TextField} from '@material-ui/core';
import {toEither} from 'Core/models/Async';
import {
  getEsrTotalRecodsChartDetails,
  getFileVolumeChartDetail,
  getProcessingAccuracyChartDetails,
  getEmployers,
  clearProcessingAccuracyChartDetails,
  clearEsrTotalRecordsChartDetails,
  clearFileVolumeChartDetails,
} from 'Enrollment/ducks/enrollmentFileProcessing';
import {pipe} from 'fp-ts/lib/pipeable';
import React, {useEffect, useState} from 'react';
import {useDispatch, useSelector} from 'react-redux';
import {renderAsync} from 'Shared/components/RenderAsync';
import RequireScope from 'Shared/components/RequireScope';
import {Scope} from 'Shared/constants/Scope';
import {selectUserScopes} from 'Users/selectors/userProfileSelectors';
import EnrollmentProcessingAccuracyChart from './EnrollmentProcessingAccuracyChart';
import EnrollmentTotalRecordsChart from './EnrollmentTotalRecordsChart';
import TableHeader from 'Shared/components/Table/TableHeader';
import {fold as foldEither} from 'fp-ts/lib/Either';
import {ReducerState} from 'rootReducer';
import EnrollmentFileVolumeChart from './EnrollmentFileVolumeChart';
import Autocomplete from '@material-ui/lab/Autocomplete';
import * as O from 'fp-ts/lib/Option';
import {Employer} from 'Enrollment/models/Employer';

export enum ChartType {
  Weekly = 'Weekly',
  Monthly = 'Monthly',
}
const ETSAdminDashboard = () => {
  const scopes = useSelector(selectUserScopes);
  const {processingAccuracyChartDetails, fileVolumeChartDetails, esrTotoalRecordChartDetails, employers} = useSelector(
    (state: ReducerState) => state.enrollmentFileProcessing
  );
  const [selectedEmployer, setSelectedEmployer] = useState<Employer | null>(null);
  const [selectedChartType, setSelectedChartType] = useState(ChartType.Weekly);
  const dispatch = useDispatch();
  useEffect(() => {
    dispatch(getEmployers());
  }, []);
  const employerName = pipe(
    selectedEmployer,
    O.fromNullable,
    O.map(x => x.accountNumber!),
    O.getOrElse(() => '')
  );
  useEffect(() => {
    dispatch(getFileVolumeChartDetail(selectedChartType, employerName));
    dispatch(getProcessingAccuracyChartDetails(employerName));
    dispatch(getEsrTotalRecodsChartDetails(employerName));
    return () => {
      dispatch(clearProcessingAccuracyChartDetails());
      dispatch(clearFileVolumeChartDetails());
      dispatch(clearEsrTotalRecordsChartDetails());
    };
  }, [selectedEmployer]);

  const handleChartTypeChange = (value: ChartType) => {
    setSelectedChartType(value);
    dispatch(getFileVolumeChartDetail(value, employerName));
  };
  return (
    <div className="enrollment-dashboard fade">
      <div className="enrollment-dashboard__header">
        <Grid container justify="space-between">
          <Grid item>
            <TableHeader
              id="enrollment-dashboard-title"
              className="enrollment-dashboard__dashboard-title"
              title="ETS Admin Dashboard"
            />
          </Grid>
          <Grid item>
            {pipe(
              toEither(employers),
              foldEither(renderAsync, employers => (
                <div className="enrollment-dashboard__filters">
                  <label className="enrollment-dashboard__filters--title">Filter:</label>{' '}
                  <Autocomplete
                    className="enrollment-dashboard__filters--employers"
                    id="enrollment-dashboard-employers-filter"
                    options={employers}
                    getOptionLabel={option => option.accountNumber!}
                    value={selectedEmployer}
                    onChange={(e, newValue) => setSelectedEmployer(newValue)}
                    openOnFocus
                    renderInput={params => <TextField {...params} label="Employer" variant="outlined" />}
                  />
                </div>
              ))
            )}
          </Grid>
        </Grid>
      </div>

      <div className="enrollment-dashboard__scope-categories">
        <div className="scope-category">
          <Grid container spacing={4}>
            <Grid item xs={12}>
              <div className="enrollment-dashboard__enrollment-charts">
                <Grid container spacing={6}>
                  <RequireScope scopes={scopes} scope={Scope.ViewProcessingAccuracyChart}>
                    <Grid item xs={12} md={12} xl={6}>
                      <div className="enrollment-dashboard__days-taken-charts">
                        {pipe(
                          toEither(processingAccuracyChartDetails),
                          foldEither(renderAsync, processingAccuracyDetails => (
                            <EnrollmentProcessingAccuracyChart
                              processingAccuracyChartDetails={processingAccuracyDetails}
                            />
                          ))
                        )}
                      </div>
                    </Grid>
                  </RequireScope>
                  <RequireScope scopes={scopes} scope={Scope.ViewLifeCycleReportChart}>
                    <Grid item xs={12} md={12} xl={6}>
                      {pipe(
                        toEither(fileVolumeChartDetails),
                        foldEither(renderAsync, lifeCycleReportDetailData => (
                          <EnrollmentFileVolumeChart
                            lifeCycleReportChartDetails={lifeCycleReportDetailData}
                            handleChartTypeChange={handleChartTypeChange}
                            selectedChartType={selectedChartType}
                          />
                        ))
                      )}
                    </Grid>
                  </RequireScope>
                  <RequireScope scopes={scopes} scope={Scope.ViewTotalRecordsChart}>
                    <Grid item xs={12} md={6}>
                      {pipe(
                        toEither(esrTotoalRecordChartDetails),
                        foldEither(renderAsync, totalRecordsDetailData => (
                          <EnrollmentTotalRecordsChart totalRecordsChartDetails={totalRecordsDetailData} />
                        ))
                      )}
                    </Grid>
                  </RequireScope>
                </Grid>
              </div>
            </Grid>
          </Grid>
        </div>
      </div>
    </div>
  );
};

export default ETSAdminDashboard;
